import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

// ============================================
// Container & Layout
// ============================================
export const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  width: '100%',
});

export const HeaderWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
});

export const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const SearchInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const SearchTitle = styled(Typography)({
  width: '110px',
  fontSize: '20px',
  fontWeight: 500,
  marginRight: '35px',
});

export const CheckboxRow = styled(Box)({
  display: 'flex',
});

// ============================================
// Sport Filter Section
// ============================================
export const SportFilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '24px',
  minHeight: '24px',
});

export const SportFilterLabel = styled(Typography)({
  width: '98px',
  minWidth: '98px',
  fontWeight: 500,
  fontSize: '14px',
  marginRight: '8px',
});

export const SportFilterContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const AllCheckboxWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '70px',
  minWidth: '70px',
});

export const AllCheckboxLabel = styled(FormControlLabel)({
  marginRight: '0',
  marginLeft: '0',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
});

export const Label = styled(Typography)({
  fontSize: '14px',
});

export const StyledCheckbox = styled(Checkbox)({
  padding: '0',
  marginRight: '4px',
});

export const SportFilterDivider = styled('span')({
  display: 'inline-block',
  width: '2px',
  height: '20px',
  backgroundColor: '#ddd',
  marginLeft: '24px',
  marginRight: '24px',
});

export const CheckboxLabel = styled(FormControlLabel)({
  marginLeft: '0',
  width: '100px',
  marginRight: '11.5px',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
});

// ============================================
// Direct Input Section
// ============================================
export const DirectInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const DirectInputLabel = styled(Typography)({
  fontSize: '14px',
  marginRight: '4px',
});

export const DirectInputField = styled('input')({
  width: '48px',
  height: '24px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  textAlign: 'center',
  fontSize: '14px',
  '&:focus': {
    outline: 'none',
    borderColor: '#0F7F85',
  },
});

export const DirectInputUnit = styled(Typography)({
  fontSize: '14px',
  marginLeft: '4px',
});

export const ActionButtonRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: 16,
});

export const FilterRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 16,
});

export const FilterLeftBox = styled(Box)({});

export const FilterRightBox = styled(Box)({});

// ============================================
// Buttons
// ============================================
export const SearchButton = styled(Button)({
  background: '#707070',
  width: '62px',
  height: 40,
  marginLeft: 8,
  '&:hover': {
    background: '#707070',
    color: '#fff',
  },
});

export const CreateButton = styled(Button)({
  height: '40px',
  borderRadius: '10px',
  background: '#15AFB7',
  color: '#fff',
  border: 'none',
  fontSize: '16px',
  fontWeight: 'bold',
  gap: '5px',
  '&:hover': {
    background: '#15AFB7',
    color: '#fff',
    border: 'none',
  },
});

export const ApplyButton = styled(Button)({
  borderRadius: '8px',
  minWidth: 220,
  height: 40,
});

export const ClearButton = styled(Button)({
  minWidth: 140,
  height: 40,
  borderRadius: '8px',
  marginLeft: 8,
});

// ============================================
// Table
// ============================================
export const Divider = styled('hr')({
  border: '1px solid #e1e1e1',
});

export const StyledTableContainer = styled(TableContainer)({
  marginTop: '10px',
  border: '1px solid #ddd',
  borderRadius: '5px',
});

export const StyledTableHead = styled(TableHead)({
  background: '#F0F2F5',
  font: 'normal normal medium 12px/14px Pretendard',
});

export const StyledTableRow = styled(TableRow)({
  height: '40px',
});

export const StyledTableHeaderCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  fontSize: '12px',
  fontWeight: 500,
});

export const StyledTableHeaderCellLast = styled(TableCell)({
  fontSize: '12px',
  fontWeight: 500,
});

export const StyledTableBodyRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'ended',
})(({ ended }) => ({
  height: '40px',
  textDecoration: 'none',
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
  ...(ended && {
    backgroundColor: '#f9f9f9',
    '& td, & th': {
      color: '#bbb',
    },
  }),
  '&:hover': {
    backgroundColor: ended ? '#f3f3f3' : '#f9fafb',
  },
}));

export const StyledTableBodyCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  fontSize: '12px',
});

export const StyledTableBodyCellLast = styled(TableCell)({
  fontSize: '12px',
});

export const StatusText = styled('span')(({ status }) => {
  const styles = {
    APPROVED: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
    ON_SALE: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
    APPROVAL_PENDING: { color: '#5A5A5A', backgroundColor: '#f0f0f0' },
    REFUSED: { color: '#FFB659', backgroundColor: '#fff5e6' },
    EXPIRED: { color: '#EE735A', backgroundColor: '#fef0ed' },
    SALE_END: { color: '#EE735A', backgroundColor: '#fef0ed' },
  };
  const s = styles[status] || { color: '#262626', backgroundColor: '#f5f5f5' };
  return {
    display: 'inline-block',
    fontWeight: 600,
    fontSize: '11px',
    padding: '1px 8px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    color: s.color,
    backgroundColor: s.backgroundColor,
  };
});

// ============================================
// Pagination
// ============================================
export const PaginationWrapper = styled(Stack)({
  marginTop: '20px',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

export const StyledPagination = styled(Pagination)({
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium': {
    background: '#0F7F85',
    border: 'none',
    height: '24px',
    minWidth: '24px',
  },
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium.Mui-selected': {
    background: '#0F7F85',
    color: '#fff',
  },
  '.MuiPaginationItem-icon': {
    color: '#fff',
  },
});

// ============================================
// Form Components Styles
// ============================================
export const searchInputStyle = {
  width: '370px',
};

export const sortOrderDropdownStyle = {
  minWidth: 100,
  height: 40,
};

export const sortTypeDropdownStyle = {
  minWidth: 120,
  height: 40,
  mx: 1,
};

export const limitDropdownStyle = {
  width: 150,
};
